<template>
  <v-card class="secondary">
    <v-card-title class="primary white--text">
      Extend Due Date
    </v-card-title>
    <v-card-text>
      <v-container>
        <!-- Display selected information -->
        <v-row>
          <v-col cols="12">
            <v-card class="mb-4" outlined elevation="2">
              <v-card-text class="pa-4">
                <div class="d-flex align-center mb-3">
                  <span class="subtitle-1 font-weight-medium"
                    >Summary</span
                  >
                </div>

                <v-divider class="mb-3"></v-divider>

                <v-row class="mt-2">
                  <v-col cols="12" md="4" class="py-0">
                    <div class="d-flex align-center">
                      <div>
                        <div class="caption grey--text">
                          Student Invoices Count
                        </div>
                        <div class="title font-weight-bold">
                          {{ selectedCount }}
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="4" class="py-0">
                    <div class="d-flex align-center">
                      <div>
                        <div class="caption grey--text">Fee Type(s)</div>
                        <div class="body-2 font-weight-medium">
                          <v-chip
                            v-for="type in feeTypes"
                            :key="type"
                            x-small
                            class="mr-1 mb-1"
                            color="primary"
                            outlined
                          >
                            {{ type }}
                          </v-chip>
                          <span v-if="!feeTypes?.length" class="grey--text"
                            >Not selected</span
                          >
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="4" class="py-0">
                    <div class="d-flex align-center">
                      <div>
                        <div class="caption grey--text">Fee Raised Batch</div>
                        <div class="body-1 font-weight-bold">
                          {{ raisedBatch || "Not selected" }}
                        </div>
                      </div>
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                    class="py-0 mt-2"
                    v-if="existingFromDates.length"
                  >
                    <div class="d-flex align-center">
                      <div>
                        <div class="caption grey--text">From Date</div>
                        <div class="body-2 font-weight-medium">
                          <v-chip
                            v-for="date in existingFromDates"
                            :key="date"
                            x-small
                            class="mr-1 mb-1"
                            color="info"
                            outlined
                          >
                            {{ formatDate(date) }}
                          </v-chip>
                        </div>
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="displayDate"
                  label="Due Date"
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  hide-details="auto"
                  clearable
                  placeholder="Select due date"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.to_date"
                @input="toDateMenu = false"
                :min="minDate"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-row v-if="existingToDates.length">
          <v-col cols="12">
            <v-alert type="warning" dense outlined class="mt-2 mb-0">
              <div class="caption">
                <strong>Note:</strong> Selected student invoices have the
                following due dates. Setting a new due date will override these:
              </div>
              <div class="d-flex flex-wrap mt-1">
                <v-chip
                  v-for="date in existingToDates"
                  :key="date"
                  class="mr-1 mb-1"
                  small
                  outlined
                  color="warning"
                >
                  {{ formatDate(date) }}
                </v-chip>
              </div>
            </v-alert>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn color="error" text @click="close">Cancel</v-btn>
      <v-btn
        color="accent"
        :loading="loading"
        :disabled="!isFormValid"
        @click="proceed"
      >
        Proceed
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  props: {
    selectedApplicants: {
      type: Array,
      required: true,
    },
    selectedCount: {
      type: Number,
      default: 0,
    },
    feeTypes: {
      type: Array,
      default: () => [],
    },
    raisedBatch: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      form: {
        to_date: null,
      },
      toDateMenu: false,
      loading: false,
      displayDate: null,
    };
  },
  watch: {
    "form.to_date": {
      handler(newVal) {
        if (newVal) {
          const [year, month, day] = newVal.split("-");
          this.displayDate = `${day}-${month}-${year}`;
        } else {
          this.displayDate = null;
        }
      },
      immediate: true,
    },
  },
  computed: {
    minDate() {
      return new Date().toISOString().substr(0, 10);
    },
    isFormValid() {
      return this.form.to_date;
    },
    existingToDates() {
      if (!this.selectedApplicants || !this.selectedApplicants.length)
        return [];

      const dates = this.selectedApplicants
        .map((item) => item.to_date)
        .filter((date) => date && date !== null && date !== "")
        .filter((value, index, self) => self.indexOf(value) === index);

      return dates.sort((a, b) => {
        const dateA = this.parseDateString(a);
        const dateB = this.parseDateString(b);
        return dateA - dateB;
      });
    },
    existingFromDates() {
      if (!this.selectedApplicants || !this.selectedApplicants.length)
        return [];

      const dates = this.selectedApplicants
        .map((item) => item.from_date)
        .filter((date) => date && date !== null && date !== "")
        .filter((value, index, self) => self.indexOf(value) === index);

      return dates.sort((a, b) => {
        const dateA = this.parseDateString(a);
        const dateB = this.parseDateString(b);
        return dateA - dateB;
      });
    },
  },
  methods: {
    parseDateString(dateStr) {
      if (!dateStr) return new Date(0);

      if (typeof dateStr === "string" && dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = dateStr.split("-");
        return new Date(year, month - 1, day);
      }

      if (typeof dateStr === "string" && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return new Date(dateStr);
      }

      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date;
      }

      return new Date(0);
    },

    formatDate(dateStr) {
      if (!dateStr) return "";

      if (typeof dateStr === "string" && dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [day, month, year] = dateStr.split("-");
        return `${day}/${month}/${year}`;
      }

      if (typeof dateStr === "string" && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = dateStr.split("-");
        return `${day}/${month}/${year}`;
      }

      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString("en-GB");
      }

      return dateStr;
    },

    close() {
      this.$emit("closeDialog");
    },

    proceed() {
      if (!this.isFormValid) return;
      this.loading = true;

      this.$emit("configure", {
        to_date: this.form.to_date,
      });

      setTimeout(() => {
        this.loading = false;
        this.close();
      }, 500);
    },
  },
};
</script>

<style scoped>
.v-chip {
  cursor: default;
}
</style>
